// @ts-strict-ignore
import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectNavigationModel, ProjectNavigationResult, ProjectNavigationTab } from '../project-navigation.model';
import { MoveOrCopyOperationResult, ProjectNavigationService } from '../service/project-navigation.service';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { UserModel } from '@user/model/user.model';

@Component({
  standalone: false,
  templateUrl: './move-project.component.html',
  selector: 'app-move-project'
})
export class MoveProjectComponent implements OnInit {
  @Input() itemToMove: ProjectGroupVO | ProjectHeadVO;
  @Input() initialTab: ProjectNavigationTab;
  @Input() isCompositeArticleProject: boolean;

  @Output() public finish = new EventEmitter<ProjectNavigationResult<MoveOrCopyOperationResult>>();

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  public model: ProjectNavigationModel;

  public submitEnabled = false;

  public lastSelectedGroupIdToMoveOrCopy: number;

  public constructor(
    private projectNavigationService: ProjectNavigationService,
    private userModel: UserModel
  ) {}

  public getItemName(): string {
    if(this.itemToMove instanceof ProjectGroupVO) {
      return this.itemToMove.groupName;
    }
    if(this.itemToMove instanceof ProjectHeadVO) {
      return this.itemToMove.name;
    }
  }

  public ngOnInit() {
    this.lastSelectedGroupIdToMoveOrCopy = this.itemToMove instanceof ProjectGroupVO ? this.itemToMove.id : this.itemToMove.projectGroup.id;
    this.model = new ProjectNavigationModel();
    this.model.compositeCheckBoxValue = this.isCompositeArticleProject;
    this.model.newConfigName = this.itemToMove instanceof ProjectGroupVO ? this.itemToMove.groupName : this.itemToMove.name;
    this.model.initialTab = this.initialTab;
    this.model.currentModel = {
      importType: null,
      lastSelectedGroupId: this.lastSelectedGroupIdToMoveOrCopy,
      isMoveOrCopy: true,
      preselectGroup: true,
      visibleSharedProjects: true,
      visibleHiddenProjects: true
    };

    this.model.canNavigateIntoProjectGroups =  this.itemToMove instanceof ProjectHeadVO;

    this.projectNavigationService.openPreselectedProjectGroup(this.model);
  }

  public moveToProjectGroup?: ProjectGroupVO;

  public updateSubmitEnabled() {
    this.moveToProjectGroup = this.updateMoveToLocation()
    if (this.moveToProjectGroup != null && this.moveToProjectGroup.id < 0 && this.moveToProjectGroup.groupOwner.userId !== this.userModel.user.id) {
      this.submitEnabled = false;
    } else {
      this.submitEnabled = !!this.moveToProjectGroup;
    }
  }

  private updateMoveToLocation(): ProjectGroupVO {
    if (this.itemToMove instanceof ProjectGroupVO) {
      if (ProjectGroupUtils.isFolderGroup(this.itemToMove)) {
        return this.getMoveToFolder(this.itemToMove);
      } else {
        return this.getMoveToProjectGroup(this.itemToMove);
      }
    }
    if (this.itemToMove instanceof ProjectHeadVO) {
      return this.getMoveToProjectHead();
    }
    return null;
  }

  private getMoveToFolder(itemToMove: ProjectGroupVO): ProjectGroupVO {
    // check if the given folder can be moved into another (selected) folder
    if (this.isSelectedDefaultGroup() && this.model.currentSelectedItem instanceof ProjectGroupVO) {
      const isItemToMoveSelected = this.model.currentSelectedItem.id === this.itemToMove.id;
      const isParentGroupSelected = itemToMove.parentGroup && itemToMove.parentGroup.id === this.model.currentSelectedItem.id;
      const isChildGroupSelected = ProjectGroupUtils.isTargetProjectGroupChildOfProjectGroup(this.model.currentSelectedItem, itemToMove);
      if (!isItemToMoveSelected && !isParentGroupSelected && !isChildGroupSelected) {
        return this.model.currentSelectedItem;
      }
    }
    // check if the given folder can be moved into opened folder
    if ((this.isSelectedConfigurationGroup() || this.isSelectedProject() || this.isNothingSelected()) && this.model.currentProjectFolder) {
      const isItemToMoveOpened = this.model.currentProjectFolder.id === this.itemToMove.id;
      const isParentGroupOpened = itemToMove.parentGroup
        ? itemToMove.parentGroup.id === this.model.currentProjectFolder.id
        : this.model.currentProjectFolder.id === ProjectNavigationModel.ROOT_USER_ID;
      const isChildFolderOpened = ProjectGroupUtils.isTargetProjectGroupChildOfProjectGroup(this.model.currentProjectFolder, itemToMove);
      if (!isItemToMoveOpened && !isParentGroupOpened && !isChildFolderOpened) {
        return this.model.currentProjectFolder;
      }
    }
  }

  private getMoveToProjectGroup(itemToMove: ProjectGroupVO): ProjectGroupVO | undefined {
    // move project into the selected folder
    if (this.isSelectedDefaultGroup()) {
      return this.model.currentSelectedItem as ProjectGroupVO;
    }

    //...or the root of any user in the shared view
    if (this.model.currentProjectFolder.groupName == ProjectNavigationModel.ROOT_NAME_SHARED) {
      return this.model.currentProjectFolder;
    }

    //...or to the opened one if it's not already the parent of the given project
    const parentGroup: ProjectGroupVO = this.model.findParentGroup(itemToMove, this.userModel.user);
    if (parentGroup && this.model.currentProjectFolder && this.model.currentProjectFolder.id !== parentGroup.id) {
      return this.model.currentProjectFolder;
    }
  }


  private getMoveToProjectHead(): ProjectGroupVO {
    if (this.model.currentViewedProjectGroup) {
      return this.model.currentViewedProjectGroup;
    }
    if (this.model.currentSelectedItem instanceof ProjectHeadVO) {
      return this.model.currentSelectedItem.projectGroup;
    }
    if (this.model.currentSelectedItem && this.isSelectedConfigurationGroup()) {
      return this.model.currentSelectedItem;
    }
  }

  private isSelectedDefaultGroup(): boolean {
    return (
      this.model.currentSelectedItem &&
      this.model.currentSelectedItem instanceof ProjectGroupVO &&
      ProjectGroupUtils.isFolderGroup(this.model.currentSelectedItem)
    );
  }

  private isSelectedConfigurationGroup(): boolean {
    return this.model.currentSelectedItem instanceof ProjectGroupVO && ProjectGroupUtils.isConfigurationGroup(this.model.currentSelectedItem);
  }

  private isSelectedProject(): boolean {
    return this.model.currentSelectedItem instanceof ProjectHeadVO;
  }

  private isNothingSelected(): boolean {
    return !this.model.currentSelectedItem;
  }

  public async moveProject() {
    const res = await this.projectNavigationService
      .moveProjectOrConfiguration(this.model, this.itemToMove, this.moveToProjectGroup);
    if (!!res) {
      this.finish.emit({success: true, result: res})
    } else {
      this.finish.emit({success: false});
    }
  }

  public cancel() {
    this.finish.emit({success: true, result: undefined});
  }
}
